import React, { useState, useCallback } from 'react'
import { Link, MessageBar, MessageBarType } from 'office-ui-fabric-react';
import { ConfirmationDialog } from '../../common/ConfirmationDialog';
import EntityPicker from '../../common/inputs/EntityPicker';
import { IFindResult } from '../../common/inputs/EntityPickerInput';
import LabellableComponent from '../../common/LabellableComponent';
import { Calendar, CalendarInfo, CalendarStatus } from '../../../store/calendar/common';
import { ApplicationState } from '../../../store';
import { connect } from 'react-redux';
import { Dictionary } from '../../../entities/common';

type OwnProps = {
    readonly?: boolean,
    value?: CalendarInfo;
    onChange: (value?: CalendarInfo) => void;
};

type StateProps = {
    calendarsById: Dictionary<Calendar>;
};

type Props = OwnProps & StateProps;

const CalendarSection = (props: Props) => {
    const { readonly, value, calendarsById, onChange } = props;
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [calendarInfo, setCalendarInfo] = useState<CalendarInfo | undefined>(value);    

    React.useEffect(() => {
        if (!showConfirmation && calendarInfo?.id !== value?.id) {
            setCalendarInfo(value)
        }
    }, [showConfirmation, calendarInfo, value])

    const onEditComplete = useCallback((result: IFindResult) => {
        if (!result) {
            onChange(undefined);
            return;
        }

        setCalendarInfo({ id: result.id, name: result.name }); 
        
        if (result.id === value?.id) {
            onChange({ id: result.id, name: result.name });
        } else {
            setShowConfirmation(true);
        }
    }, [onChange, value]);

    const isInactive = !!calendarInfo && calendarsById[calendarInfo.id]?.attributes.Status === CalendarStatus.Inactive;

    return (
        <>
            <div className='section-description'>
                Select the calendar for the current project. The calendar affects the PPM Express Tasks without assignments.
                Resource plan and PPM Express Tasks with assignments are scheduled according to the resource calendar(s). Please refer to
                <Link href="https://help.ppm.express/94162-portfolio-and-project-management/2082039-project-settings#section-7" target="_blank"> this article</Link> for more details.
            </div>
            {isInactive && <MessageBar messageBarType={MessageBarType.warning} isMultiline>
                The selected calendar is inactive
            </MessageBar>}
            <LabellableComponent className="field-container" label="Calendar" required>
                <EntityPicker
                    value={calendarInfo}
                    searchPath="api/calendar/find"
                    readOnly={readonly}
                    disabled={readonly}
                    inputProps={{ required: true }}
                    onEditComplete={onEditComplete}
                />
            </LabellableComponent>
            {showConfirmation && calendarInfo &&
                <ConfirmationDialog
                    dialogContentProps={{
                        isMultiline: true,
                        title: "Changing Project Calendar",
                        subText: `Are you sure you want to change the Project Calendar? `
                    }}
                    onNo={() => setCalendarInfo(value)}
                    onDismiss={() => setShowConfirmation(false)}
                    onYes={() => onChange(calendarInfo)}>
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline>
                        The project’s PPM Express tasks without assignments will be rescheduled according to the new calendar.
                        Resource plan and PPM Express Tasks with assignments will not be affected, as they are scheduled according to the resource calendar(s).
                    </MessageBar>
                </ConfirmationDialog>}
        </>);
}

function mapStateToProps(state: ApplicationState): StateProps {
    return {
        calendarsById: state.calendars.byId,
    };
}

export default connect(mapStateToProps)(CalendarSection);
